import { type CallWindow, type Event } from '@kalos/kalos-rpc';
import { format } from 'date-fns';

import { getBlockedJobTypesForWindow } from '../../../../hooks/react-query/useWindowBlockQuery';
import { normalizeTime } from '../../../../tools/timeUtils';
export type TimeWindowResolutionType = 'preset' | 'custom' | 'none';
export type TimeWindowResolutionReason =
  | 'currentWindowId'
  | 'formTimes'
  | 'existingEvent'
  | 'initialWindow'
  | 'firstAvailable'
  | 'custom'
  | 'none';

export type TimeWindowResolution = {
  effectiveTimeWindow: CallWindow | null;
  nextWindowId: number | null;
  type: TimeWindowResolutionType;
  reason: TimeWindowResolutionReason;
};

type ResolveTimeWindowParams = {
  dayWindowsAscending: CallWindow[];
  callWindows: CallWindow[];
  selectedDate: string;
  currentWindowId?: number;
  formTimeStarted?: string;
  formTimeEnded?: string;
  existingEvent?: Event;
  initialTimeWindowId?: number;
  isEditing: boolean;
  windowBlocks?: Event[];
  totalJobTypeCount?: number;
};

export const resolveTimeWindow = ({
  dayWindowsAscending,
  callWindows,
  selectedDate,
  currentWindowId,
  formTimeStarted,
  formTimeEnded,
  existingEvent,
  initialTimeWindowId,
  isEditing,
  windowBlocks = [],
  totalJobTypeCount = 0,
}: ResolveTimeWindowParams): TimeWindowResolution => {
  if (dayWindowsAscending.length === 0) {
    return {
      effectiveTimeWindow: null,
      nextWindowId: null,
      type: 'none',
      reason: 'none',
    };
  }

  const now = new Date();
  const currentDate = format(now, 'yyyy-MM-dd');
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
  const isPastWindow = (window: CallWindow) => {
    if (selectedDate < currentDate) return true;
    if (selectedDate === currentDate) {
      const windowStartMinutes =
        parseInt(window.startTime.split(':')[0]) * 60 + parseInt(window.startTime.split(':')[1]);
      return windowStartMinutes < currentTimeInMinutes;
    }
    return false;
  };

  // Only treat a window as "blocked" for routing purposes when it is FULLY blocked
  // (all job types or an all-type block). Partially blocked windows (specific job
  // types only) are still selectable — the form will surface which types are restricted.
  const isBlocked = (window: CallWindow) => {
    const blockedIds = getBlockedJobTypesForWindow(windowBlocks, window.startTime, window.endTime);
    if (blockedIds.length === 0) return false;
    if (blockedIds.some((id) => id === 0)) return true;
    return totalJobTypeCount > 0 && blockedIds.length >= totalJobTypeCount;
  };

  // FIRST PRIORITY: currently selected window (user already chose this)
  // Allow past windows here since the user deliberately selected it
  if (currentWindowId) {
    const exactMatch = dayWindowsAscending.find((w) => w.id === currentWindowId);
    if (exactMatch) {
      return {
        effectiveTimeWindow: exactMatch,
        nextWindowId: null,
        type: 'preset',
        reason: 'currentWindowId',
      };
    }
  }

  // SECOND PRIORITY: formState times (user's current input)
  if (formTimeStarted && formTimeEnded) {
    const matchingWindow = dayWindowsAscending.find(
      (w) =>
        normalizeTime(w.startTime) === normalizeTime(formTimeStarted) &&
        normalizeTime(w.endTime) === normalizeTime(formTimeEnded),
    );

    if (matchingWindow && !isPastWindow(matchingWindow) && !isBlocked(matchingWindow)) {
      return {
        effectiveTimeWindow: matchingWindow,
        nextWindowId: matchingWindow.id,
        type: 'preset',
        reason: 'formTimes',
      };
    }
    return {
      effectiveTimeWindow: null,
      nextWindowId: null,
      type: 'custom',
      reason: 'custom',
    };
  }

  // THIRD: existing event (reschedule)
  if (existingEvent?.timeStarted && existingEvent?.timeEnded) {
    const matchingWindow = dayWindowsAscending.find(
      (w) =>
        normalizeTime(w.startTime) === normalizeTime(existingEvent.timeStarted) &&
        normalizeTime(w.endTime) === normalizeTime(existingEvent.timeEnded),
    );

    if (matchingWindow && !isPastWindow(matchingWindow) && !isBlocked(matchingWindow)) {
      return {
        effectiveTimeWindow: matchingWindow,
        nextWindowId: null,
        type: 'preset',
        reason: 'existingEvent',
      };
    }
  }

  // FOURTH: initial time window (from caller, e.g. calendar card click)
  // Skip if past or blocked -- fall through to first available
  if (initialTimeWindowId && !isEditing) {
    const initialWindow = callWindows.find((w) => w.id === initialTimeWindowId);
    if (initialWindow) {
      const matchingWindow = dayWindowsAscending.find(
        (w) =>
          normalizeTime(w.startTime) === normalizeTime(initialWindow.startTime) &&
          normalizeTime(w.endTime) === normalizeTime(initialWindow.endTime),
      );

      if (matchingWindow && !isPastWindow(matchingWindow) && !isBlocked(matchingWindow)) {
        return {
          effectiveTimeWindow: matchingWindow,
          nextWindowId: matchingWindow.id,
          type: 'preset',
          reason: 'initialWindow',
        };
      }
    }
  }

  // FALLBACK: first non-past, non-blocked window
  for (const window of dayWindowsAscending) {
    if (!isPastWindow(window) && !isBlocked(window)) {
      return {
        effectiveTimeWindow: window,
        nextWindowId: null,
        type: 'preset',
        reason: 'firstAvailable',
      };
    }
  }

  // All windows are past or blocked -- return first non-past (even if blocked)
  // so the dialog can still open and user can unblock
  for (const window of dayWindowsAscending) {
    if (!isPastWindow(window)) {
      return {
        effectiveTimeWindow: window,
        nextWindowId: null,
        type: 'preset',
        reason: 'firstAvailable',
      };
    }
  }

  // All windows are past
  return {
    effectiveTimeWindow: null,
    nextWindowId: null,
    type: 'none',
    reason: 'none',
  };
};
